export type EexLiveMarketFilter = {
  apiFamily: string;
  marketSegment: string;
  commodityCode: string;
  areaCode: string;
  dataset: string;
};

export type EexLiveMarketView = "market" | "tape" | "index" | "health";

export type EexMarketWatchlistFilter = "all" | "personal" | "company";

export type EexNaturalGasFuturesPresetRow = {
  subjectId: string;
  commodityCode: string;
  areaCode: string;
  instrumentType: string | null;
  productType: string | null;
};

export type EexMatrixProductGroup = {
  key: string;
  label: string;
  commodityCode: string;
  areaCode: string;
  profile: string | null;
  familyCode: string | null;
  currency: string | null;
  uom: string | null;
};

export type EexMatrixDeliveryPeriod = {
  key: string;
  kind: "day" | "weekend" | "week" | "month" | "quarter" | "season" | "year" | "spread";
  label: string;
  sortKey: string;
  deliveryStart: string | null;
  deliveryEnd: string | null;
};

export type EexMatrixCompatibleRow = {
  scopeId: string;
  subjectId: string;
  apiFamily: string;
  productGroup: EexMatrixProductGroup | null;
  deliveryPeriod: EexMatrixDeliveryPeriod | null;
};

export type EexMatrixQuoteRow = EexMatrixCompatibleRow & {
  coverageState: "unknown" | "quoted" | "unquoted" | "omitted";
  marketTimestamp: string | null;
  receivedAt: string;
};

export type EexMatrixSettlementRow = EexMatrixCompatibleRow & {
  price: string;
  tradingDate: string;
  receivedAt: string;
  source: "eex_datasource" | "ecc_span";
};

export type EexMarketMatrix<
  I extends EexMatrixCompatibleRow,
  Q extends EexMatrixQuoteRow,
  S extends EexMatrixSettlementRow = EexMatrixSettlementRow,
> = {
  groups: Array<EexMatrixProductGroup & {
    cells: Map<string, { instrument: I; quote: Q | null; settlement: S | null }>;
  }>;
  periods: EexMatrixDeliveryPeriod[];
};

export type EexMatrixBoardConfiguration = {
  productGroupKeys: string[];
  periodMode: "follow_universe" | "custom";
  deliveryPeriodKeys: string[];
};

export type EexMarketSearchableRow = {
  providerKey: string;
  displayName: string | null;
  commodityCode: string;
  areaCode: string;
  personalWatchlisted: boolean;
  companyWatchlisted: boolean;
};

/**
 * Product tabs own their dataset identity. A dataset chosen while inspecting
 * feed health must never leak into a different tab and silently empty it.
 */
export function eexFilterForView(
  filter: EexLiveMarketFilter,
  view: EexLiveMarketView
): EexLiveMarketFilter {
  if (view === "health") return { ...filter };
  if (view === "index") {
    return {
      ...filter,
      apiFamily: "index",
      marketSegment: "index",
      dataset: "index_publications",
    };
  }
  return {
    ...filter,
    apiFamily: filter.apiFamily === "index" ? "" : filter.apiFamily,
    marketSegment: filter.marketSegment === "index" ? "" : filter.marketSegment,
    dataset: view === "market" ? "top_of_book" : "public_trades",
  };
}

export function eexCachedPath(
  endpoint: string,
  filter: EexLiveMarketFilter,
  extra: Record<string, string | number | null | undefined> = {}
): string {
  const params = new URLSearchParams();
  if (filter.apiFamily) params.set("apiFamily", filter.apiFamily);
  if (filter.marketSegment) params.set("marketSegment", filter.marketSegment);
  if (filter.commodityCode) params.set("commodityCode", filter.commodityCode);
  if (filter.areaCode) params.set("areaCode", filter.areaCode);
  if (filter.dataset) params.set("dataset", filter.dataset);
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const query = params.toString();
  return `/eex/market-data/${endpoint}${query ? `?${query}` : ""}`;
}

export function eexPublicEventWindow(now: Date, hours = 24): { from: string; to: string } {
  if (!Number.isFinite(now.getTime()) || !Number.isFinite(hours) || hours <= 0 || hours > 24 * 31) {
    throw new Error("invalid EEX public-event window");
  }
  return {
    from: new Date(now.getTime() - hours * 60 * 60 * 1_000).toISOString(),
    to: now.toISOString(),
  };
}

export function eexObservationAgeLabel(receivedAt: string, now = new Date()): string {
  const received = Date.parse(receivedAt);
  if (!Number.isFinite(received) || !Number.isFinite(now.getTime())) return "unknown";
  const seconds = Math.max(0, Math.floor((now.getTime() - received) / 1_000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}

/**
 * Subtract provider decimal strings without routing market prices through
 * binary floating point. EEX values are ordinary fixed-point decimals; an
 * unexpected shape is displayed as unavailable instead of being guessed.
 */
export function eexMarketSpread(ask: string | null, bid: string | null): string | null {
  const parsedAsk = parseFixedDecimal(ask);
  const parsedBid = parseFixedDecimal(bid);
  if (!parsedAsk || !parsedBid) return null;

  const scale = Math.max(parsedAsk.scale, parsedBid.scale);
  const askUnscaled = parsedAsk.unscaled * 10n ** BigInt(scale - parsedAsk.scale);
  const bidUnscaled = parsedBid.unscaled * 10n ** BigInt(scale - parsedBid.scale);
  return formatFixedDecimal(askUnscaled - bidUnscaled, scale);
}

export function eexHealthHeadline(states: readonly string[]): "live" | "warning" | "disconnected" {
  if (states.length === 0 || states.every((state) => state === "disconnected")) return "disconnected";
  return states.every((state) => state === "live") ? "live" : "warning";
}

export function eexMarketSearchMatches(
  row: Pick<EexMarketSearchableRow, "providerKey" | "displayName" | "commodityCode" | "areaCode">,
  search: string
): boolean {
  const query = search.trim().toLocaleLowerCase();
  if (!query) return true;
  const searchable = [row.displayName, row.providerKey, row.commodityCode, row.areaCode]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLocaleLowerCase();
  return query.split(/\s+/).every((token) => searchable.includes(token));
}

export function eexMarketWatchlistMatches(
  row: Pick<EexMarketSearchableRow, "personalWatchlisted" | "companyWatchlisted">,
  filter: EexMarketWatchlistFilter
): boolean {
  if (filter === "personal") return row.personalWatchlisted;
  if (filter === "company") return row.companyWatchlisted;
  return true;
}

/**
 * The provider calls the Austrian CEGH derivatives area CEGHVTP. Keep the
 * preset deliberately limited to outright futures so enabling the two gas
 * curves does not silently add thousands of calendar/inter-product spreads.
 */
export function eexNaturalGasFuturesPresetSubjectIds(
  rows: readonly EexNaturalGasFuturesPresetRow[]
): string[] {
  const hubs = new Set(["TTF", "CEGHVTP"]);
  return rows
    .filter((row) =>
      row.commodityCode.trim().toUpperCase() === "NATGAS"
      && hubs.has(row.areaCode.trim().toUpperCase())
      && row.instrumentType?.trim().toLowerCase() === "simple instrument"
      && row.productType?.trim().toLowerCase() === "future"
    )
    .map((row) => row.subjectId);
}

/**
 * Pivot provider rows into a stable trading board. A listed period remains a
 * row even when another selected product has no corresponding contract.
 */
export function buildEexMarketMatrix<
  I extends EexMatrixCompatibleRow,
  Q extends EexMatrixQuoteRow,
  S extends EexMatrixSettlementRow = EexMatrixSettlementRow,
>(
  instruments: readonly I[],
  quotes: readonly Q[],
  settlements: readonly S[] = []
): EexMarketMatrix<I, Q, S> {
  const quoteByInstrument = new Map<string, Q>();
  for (const quote of quotes) {
    const key = matrixSubjectKey(quote);
    const current = quoteByInstrument.get(key);
    if (!current || compareMatrixQuotes(quote, current) > 0) quoteByInstrument.set(key, quote);
  }
  const settlementByInstrument = new Map<string, S>();
  for (const settlement of settlements) {
    const key = matrixSubjectKey(settlement);
    const current = settlementByInstrument.get(key);
    if (!current || compareMatrixSettlements(settlement, current) > 0) {
      settlementByInstrument.set(key, settlement);
    }
  }
  type MatrixCell = { instrument: I; quote: Q | null; settlement: S | null };
  const groups = new Map<string, EexMatrixProductGroup & { cells: Map<string, MatrixCell> }>();
  const periods = new Map<string, EexMatrixDeliveryPeriod>();
  for (const row of instruments) {
    if (!row.productGroup || !row.deliveryPeriod) continue;
    const group = groups.get(row.productGroup.key)
      ?? { ...row.productGroup, cells: new Map<string, MatrixCell>() };
    const cell = {
      instrument: row,
      quote: quoteByInstrument.get(matrixSubjectKey(row)) ?? null,
      settlement: settlementByInstrument.get(matrixSubjectKey(row)) ?? null,
    };
    const current = group.cells.get(row.deliveryPeriod.key);
    if (!current || compareMatrixCells(cell, current) > 0) group.cells.set(row.deliveryPeriod.key, cell);
    groups.set(group.key, group);
    periods.set(row.deliveryPeriod.key, row.deliveryPeriod);
  }
  return {
    groups: [...groups.values()].sort((a, b) =>
      a.commodityCode.localeCompare(b.commodityCode)
      || a.label.localeCompare(b.label)
      || a.key.localeCompare(b.key)
    ),
    periods: [...periods.values()].sort((a, b) =>
      a.sortKey.localeCompare(b.sortKey) || a.key.localeCompare(b.key)
    ),
  };
}

export function eexDefaultMatrixConfiguration<I extends EexMatrixCompatibleRow, Q extends EexMatrixQuoteRow>(
  matrix: EexMarketMatrix<I, Q>
): EexMatrixBoardConfiguration {
  const priorityAreas = ["RO", "HU", "DE", "TTF", "CEGHVTP", "GR", "IT"];
  const boardCandidates = matrix.groups
    .filter((group) => group.profile === "baseload" || group.commodityCode.toLocaleUpperCase().includes("GAS"))
    .sort((a, b) => {
      const aPriority = priorityAreas.indexOf(a.areaCode.toLocaleUpperCase());
      const bPriority = priorityAreas.indexOf(b.areaCode.toLocaleUpperCase());
      const aRank = aPriority < 0 ? priorityAreas.length : aPriority;
      const bRank = bPriority < 0 ? priorityAreas.length : bPriority;
      return aRank - bRank
        || quotedCellCount(b) - quotedCellCount(a)
        || a.label.localeCompare(b.label)
        || a.key.localeCompare(b.key);
    });
  const productGroups: typeof matrix.groups = [];
  const selectedMarkets = new Set<string>();
  for (const group of boardCandidates) {
    const marketKey = `${group.commodityCode}:${group.areaCode}:${group.profile ?? "standard"}`;
    if (selectedMarkets.has(marketKey)) continue;
    productGroups.push(group);
    selectedMarkets.add(marketKey);
    if (productGroups.length === 7) break;
  }
  if (productGroups.length === 0) productGroups.push(...matrix.groups.slice(0, 6));
  return {
    productGroupKeys: productGroups.map((group) => group.key),
    periodMode: "follow_universe",
    // Resolved by the board against the current company universe. Keeping the
    // stored list empty prevents a rolling universe from becoming a snapshot.
    deliveryPeriodKeys: [],
  };
}

const DEFAULT_MATRIX_PERIOD_LIMITS: Partial<Record<EexMatrixDeliveryPeriod["kind"], number>> = {
  day: 11,
  weekend: 2,
  week: 3,
  month: 7,
  quarter: 6,
  season: 3,
  year: 3,
};

/**
 * Resolve the compact board-row preset against today's company universe.
 * The stored preference remains an intent, so the chosen contracts advance
 * automatically rather than freezing today's exact delivery-period keys.
 */
export function eexDefaultMatrixDeliveryPeriodKeys(
  periods: readonly EexMatrixDeliveryPeriod[],
  asOf = new Date()
): string[] {
  const activePeriods = eexActiveMatrixDeliveryPeriods(periods, asOf);
  const selectedByKind = new Map<EexMatrixDeliveryPeriod["kind"], number>();
  return activePeriods.flatMap((period) => {
    const limit = DEFAULT_MATRIX_PERIOD_LIMITS[period.kind] ?? 0;
    const selected = selectedByKind.get(period.kind) ?? 0;
    if (selected >= limit) return [];
    selectedByKind.set(period.kind, selected + 1);
    return [period.key];
  });
}

/**
 * Resolve a personal matrix layout against the active company universe.
 *
 * Custom rows preserve the selected tenors and row counts, but never pin a
 * delivery period after it has ended. An expired week, month, quarter, etc.
 * is replaced by the next available live period of that same tenor.
 */
export function eexCustomMatrixDeliveryPeriodKeys(
  periods: readonly EexMatrixDeliveryPeriod[],
  configuredKeys: readonly string[],
  asOf = new Date()
): string[] {
  const periodByKey = new Map(periods.map((period) => [period.key, period]));
  const configured = [...new Set(configuredKeys)].flatMap((key) => {
    const period = periodByKey.get(key);
    return period ? [period] : [];
  });
  const requiredByKind = new Map<EexMatrixDeliveryPeriod["kind"], number>();
  for (const period of configured) {
    requiredByKind.set(period.kind, (requiredByKind.get(period.kind) ?? 0) + 1);
  }

  const activePeriods = eexActiveMatrixDeliveryPeriods(periods, asOf);
  const activeKeys = new Set(activePeriods.map((period) => period.key));
  const resolved = new Set(
    configured
      .filter((period) => activeKeys.has(period.key))
      .map((period) => period.key)
  );
  const selectedByKind = new Map<EexMatrixDeliveryPeriod["kind"], number>();
  for (const period of activePeriods) {
    if (resolved.has(period.key)) {
      selectedByKind.set(period.kind, (selectedByKind.get(period.kind) ?? 0) + 1);
    }
  }

  for (const period of activePeriods) {
    const required = requiredByKind.get(period.kind) ?? 0;
    const selected = selectedByKind.get(period.kind) ?? 0;
    if (selected >= required || resolved.has(period.key)) continue;
    resolved.add(period.key);
    selectedByKind.set(period.kind, selected + 1);
  }

  return periods.filter((period) => resolved.has(period.key)).map((period) => period.key);
}

function eexActiveMatrixDeliveryPeriods(
  periods: readonly EexMatrixDeliveryPeriod[],
  asOf: Date
): EexMatrixDeliveryPeriod[] {
  const asOfDay = asOf.toISOString().slice(0, 10);
  return periods.filter((period) => period.deliveryEnd == null || period.deliveryEnd >= asOfDay);
}

export function eexMarketTimestampTitle(row: {
  marketTimestamp: string | null;
  receivedAt: string;
  providerKey: string;
  freshnessState: string;
  coverageState: string;
}): string {
  const market = row.marketTimestamp ? timestampLabel(row.marketTimestamp) : "unavailable";
  const received = timestampLabel(row.receivedAt);
  return [
    `Market ${market}`,
    `received ${received}`,
    "EEX TOB",
    row.providerKey,
    `${row.freshnessState} feed · ${row.coverageState}`,
  ].join(" · ");
}

export function eexCompactDecimal(value: string | null, maximumDecimals = 3): string {
  const parsed = parseFixedDecimal(value);
  if (!parsed || !Number.isInteger(maximumDecimals) || maximumDecimals < 0) return "—";
  if (parsed.scale <= maximumDecimals) return formatFixedDecimal(parsed.unscaled, parsed.scale);
  const removedDigits = parsed.scale - maximumDecimals;
  const divisor = 10n ** BigInt(removedDigits);
  const magnitude = parsed.unscaled < 0n ? -parsed.unscaled : parsed.unscaled;
  const rounded = (magnitude + divisor / 2n) / divisor;
  return formatFixedDecimal(parsed.unscaled < 0n ? -rounded : rounded, maximumDecimals);
}

function matrixRowRank(row: EexMatrixQuoteRow): number {
  const coverage = row.coverageState === "quoted" ? 3 : row.coverageState === "unquoted" ? 2 : 1;
  const timestamp = Date.parse(row.marketTimestamp ?? row.receivedAt);
  return coverage * 10 ** 15 + (Number.isFinite(timestamp) ? timestamp : 0);
}

function matrixSubjectKey(row: Pick<EexMatrixCompatibleRow, "apiFamily" | "subjectId">): string {
  return `${row.apiFamily}:${row.subjectId}`;
}

function compareMatrixQuotes(a: EexMatrixQuoteRow, b: EexMatrixQuoteRow): number {
  return matrixRowRank(a) - matrixRowRank(b)
    || a.scopeId.localeCompare(b.scopeId);
}

function compareMatrixSettlements(a: EexMatrixSettlementRow, b: EexMatrixSettlementRow): number {
  return a.tradingDate.localeCompare(b.tradingDate)
    || a.receivedAt.localeCompare(b.receivedAt)
    || (a.source === b.source ? 0 : a.source === "eex_datasource" ? 1 : -1)
    || a.scopeId.localeCompare(b.scopeId);
}

function compareMatrixCells<
  I extends EexMatrixCompatibleRow,
  Q extends EexMatrixQuoteRow,
  S extends EexMatrixSettlementRow,
>(
  a: { instrument: I; quote: Q | null; settlement: S | null },
  b: { instrument: I; quote: Q | null; settlement: S | null }
): number {
  if (a.quote && b.quote) return compareMatrixQuotes(a.quote, b.quote);
  if (a.quote) return 1;
  if (b.quote) return -1;
  if (a.settlement && b.settlement) return compareMatrixSettlements(a.settlement, b.settlement);
  if (a.settlement) return 1;
  if (b.settlement) return -1;
  return matrixSubjectKey(a.instrument).localeCompare(matrixSubjectKey(b.instrument));
}

function quotedCellCount<
  I extends EexMatrixCompatibleRow,
  Q extends EexMatrixQuoteRow,
  S extends EexMatrixSettlementRow,
>(
  group: EexMarketMatrix<I, Q, S>["groups"][number]
): number {
  let count = 0;
  for (const cell of group.cells.values()) if (cell.quote?.coverageState === "quoted") count += 1;
  return count;
}

function timestampLabel(value: string): string {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return "unavailable";
  return `${new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(parsed)} UTC`;
}

function parseFixedDecimal(value: string | null): { unscaled: bigint; scale: number } | null {
  if (value == null) return null;
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return null;
  const fractional = match[3] ?? "";
  if (fractional.length > 18) return null;
  const magnitude = BigInt(`${match[2]}${fractional}`);
  return {
    unscaled: match[1] === "-" ? -magnitude : magnitude,
    scale: fractional.length,
  };
}

function formatFixedDecimal(unscaled: bigint, scale: number): string {
  const negative = unscaled < 0n;
  const digits = (negative ? -unscaled : unscaled).toString().padStart(scale + 1, "0");
  const whole = scale === 0 ? digits : digits.slice(0, -scale);
  const fractional = scale === 0 ? "" : digits.slice(-scale).replace(/0+$/, "");
  const magnitude = fractional ? `${whole}.${fractional}` : whole;
  return negative && unscaled !== 0n ? `-${magnitude}` : magnitude;
}
